import React, { useState, useRef, useEffect } from 'react';
import { Link, Outlet, useNavigate, useParams } from 'react-router-dom';
import { EntityCustomizationProvider, useEntityCustomization } from '../context/EntityCustomizationContext';
import { PspAuthProvider, usePspAuth } from '../context/PspAuthContext';
import '../psp-candidate.css';

export const PortalHeader = () => {
    const { friendly_url } = useParams();
    const navigate = useNavigate();
    const { entity } = useEntityCustomization();
    const { isAuthenticated, candidate, signOut } = usePspAuth();
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);
    const base = `/processos-seletivos/${friendly_url}`;

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const rawName = candidate ? (candidate.social_name || candidate.name || '') : '';
    const candidateDisplayName = String(rawName).trim().split(' ')[0] || 'Candidato';

    return (
        <header className="psp-header">
            <Link to={base} className="psp-header__brand" title="Página inicial de vagas">
                {entity && entity.logo ? (
                    <img className="psp-header__logo" src={entity.logo} alt={`Logo ${entity.entity_name}`} />
                ) : (
                    <strong>{entity ? entity.entity_name : ''}</strong>
                )}
            </Link>

            <div className="psp-header__actions">
                {isAuthenticated ? (
                    <div className="psp-dropdown" ref={dropdownRef}>
                        <button
                            type="button"
                            className={`psp-dropdown__toggle ${dropdownOpen ? 'is-active' : ''}`}
                            onClick={() => setDropdownOpen((prev) => !prev)}
                            aria-expanded={dropdownOpen}
                            aria-haspopup="true"
                        >
                            <i className="fas fa-user-circle" aria-hidden="true" />
                            <span>{candidateDisplayName}</span>
                            <i className={`fas fa-chevron-down psp-dropdown__arrow ${dropdownOpen ? 'is-open' : ''}`} aria-hidden="true" />
                        </button>

                        {dropdownOpen && (
                            <div className="psp-dropdown__menu psp-fade-in" role="menu">
                                <button
                                    type="button"
                                    className="psp-dropdown__item"
                                    role="menuitem"
                                    onClick={() => {
                                        setDropdownOpen(false);
                                        navigate(base);
                                    }}
                                >
                                    <i className="fas fa-briefcase" aria-hidden="true" />
                                    <span>Vagas disponíveis</span>
                                </button>
                                <button
                                    type="button"
                                    className="psp-dropdown__item"
                                    role="menuitem"
                                    onClick={() => {
                                        setDropdownOpen(false);
                                        navigate(`${base}/home`);
                                    }}
                                >
                                    <i className="fas fa-clipboard-list" aria-hidden="true" />
                                    <span>Minhas inscrições</span>
                                </button>
                                <button
                                    type="button"
                                    className="psp-dropdown__item"
                                    role="menuitem"
                                    onClick={() => {
                                        setDropdownOpen(false);
                                        navigate(`${base}/perfil`);
                                    }}
                                >
                                    <i className="fas fa-id-card" aria-hidden="true" />
                                    <span>Meus dados</span>
                                </button>
                                <div className="psp-dropdown__divider" />
                                <button
                                    type="button"
                                    className="psp-dropdown__item psp-dropdown__item--danger"
                                    role="menuitem"
                                    onClick={async () => {
                                        setDropdownOpen(false);
                                        await signOut();
                                        navigate(base);
                                    }}
                                >
                                    <i className="fas fa-sign-out-alt" aria-hidden="true" />
                                    <span>Sair</span>
                                </button>
                            </div>
                        )}
                    </div>
                ) : (
                    <button
                        type="button"
                        className="psp-header__user"
                        onClick={() => navigate(`${base}/login`)}
                    >
                        <i className="fas fa-user-circle" aria-hidden="true" />
                        <span>Área do Candidato</span>
                    </button>
                )}

                <div className="psp-header__divider" />

                <div className="psp-header__entity">
                    <span>Processos Seletivos</span>
                    <strong>{entity ? entity.entity_name : ''}</strong>
                </div>
            </div>
        </header>
    );
};

const PortalContent = () => {
    const { loading, error } = useEntityCustomization();
    const { loading: authLoading } = usePspAuth();

    if (loading || authLoading) {
        return <div className="psp-portal"><div className="psp-loading" role="status">Carregando...</div></div>;
    }

    if (error) {
        return (
            <div className="psp-portal">
                <main className="psp-main psp-center">
                    <div className="psp-auth-card psp-fade-in">
                        <div className="psp-auth-card__header">
                            <div className="psp-auth-card__icon"><i className="fas fa-building" aria-hidden="true" /></div>
                            <h1 className="psp-auth-card__title">Entidade não encontrada</h1>
                            <p className="psp-auth-card__subtitle">
                                O endereço acessado não corresponde a nenhuma entidade. Verifique o link e tente novamente.
                            </p>
                        </div>
                    </div>
                </main>
            </div>
        );
    }

    return (
        <div className="psp-portal">
            <PortalHeader />
            <main className="psp-main">
                <Outlet />
            </main>
        </div>
    );
};

/**
 * Layout shared by every route under /processos-seletivos/:friendly_url.
 * Provides the entity customization and the (separate) candidate session.
 */
const PortalLayout = () => (
    <EntityCustomizationProvider>
        <PspAuthProvider>
            <PortalContent />
        </PspAuthProvider>
    </EntityCustomizationProvider>
);

export default PortalLayout;
